import { Destination, Spot } from '@app/core/models';
import { CenterForAutocomplete } from '@app/core/services/centers/centers.service';
import { WebformService } from '@makiwin/ngx-drupal8-rest';
import {
  LocationValue,
  MultiLocationValue,
  MultiLocationValueType,
} from '../responsive-filter-multilocation/responsive-filter-multilocation.interfaces';
import {
  ResponsiveSearchFiltersFieldConfiguration,
  ResponsiveSearchFiltersFieldType,
  ResponsiveSearchFiltersTrackerFormatter,
} from './responsive-search-filters-configuration';

type MappedFields = {
  [key: string]: {
    fieldName: string;
    type: ResponsiveSearchFiltersFieldType;
    formatter: ResponsiveSearchFiltersTrackerFormatter;
    customFormatter?: (value: any) => any;
  };
};

/**
 * Tracker helper for the responsive search filters.
 */
export class ResponsiveSearchFiltersTracker {
  private mappedFields: MappedFields = {};
  private webFormId?: string;

  /**
   *
   * @param webformService
   * Webform service to submit the data to D8
   */
  constructor(private webformService: WebformService) {}

  /**
   * Initialize the tracker.
   *
   * @param webFormId
   * The id of the webform to submit the data.
   * @param fields
   * Configuration of the resposive search filters.
   */
  init(webFormId: string, fields: ResponsiveSearchFiltersFieldConfiguration[]) {
    this.webFormId = webFormId;
    this.mappedFields = fields
      .filter((field) => field.trackFormFieldName)
      .reduce((acc: MappedFields, field: ResponsiveSearchFiltersFieldConfiguration) => {
        acc[field.name] = {
          fieldName: field.trackFormFieldName ?? '',
          type: field.type,
          formatter: field.trackFormFieldFormatter ?? ResponsiveSearchFiltersTrackerFormatter.default,
          customFormatter: field.trackFormFieldCustomFormatter,
        };
        return acc;
      }, {} as MappedFields);
  }

  /**
   * Track the values of the responsive search filters.
   * @param values
   * Values of the filters.
   */
  track(values: any) {
    const valuesFormatted = this.prepareFormValues(values);
    if (Object.keys(valuesFormatted).length === 0) {
      return;
    }
    valuesFormatted['webform_id'] = this.webFormId;
    this.webformService.submit(valuesFormatted).subscribe(() => {});
    // convet the values to the webform values
    // send the data to D8
  }

  /**
   * Prepare, convert and format the values to send to the webform.
   *
   * @param values
   * @returns
   */
  private prepareFormValues(values: any): any {
    let formValues: any = {};
    for (const key in values) {
      if (values[key] === undefined || values[key] === null || values[key] === 0) {
        continue;
      }
      if (!this.mappedFields[key]) {
        continue;
      }
      switch (this.mappedFields[key].type) {
        case ResponsiveSearchFiltersFieldType.multiLocation:
          formValues = { ...formValues, ...this.convertMultiLocation(values[key], this.mappedFields[key].fieldName) };
          break;
        default:
          formValues = { ...formValues, ...this.convertValue(key, values[key]) };
      }
    }
    // filter only the field who has values
    // map the field name to the field name of the webform and convert it if needed
    return formValues;
  }

  /**
   * Convert a multi location value to the webform value.
   *
   * @param {MultiLocationValueé} value
   * Value to convert.
   * @param defaultLabel
   * Default label to use if the value is location.
   * @returns
   * The converted value.
   */
  private convertMultiLocation(value: MultiLocationValue, defaultLabel: string): any {
    switch (value.type) {
      case MultiLocationValueType.center:
        return { center: (value.value as CenterForAutocomplete).id };
      case MultiLocationValueType.spot:
        return { spot: (value.value as Spot).nid };
      case MultiLocationValueType.destination:
        return { destinations: (value.value as Destination).nid };
      default:
        return { [defaultLabel]: (value.value as LocationValue).formattedAddress };
    }
  }

  /**
   * Convert a value to the webform value.
   *
   * @param key
   * The key of the map of the value.
   * @param value
   * The value to convert.
   * @returns
   * The converted value.
   */
  private convertValue(key: string, value: any) {
    const fieldName = this.mappedFields[key].fieldName;
    if (this.mappedFields[key].customFormatter) {
      return { [fieldName]: this.mappedFields[key].customFormatter?.(value) };
    }
    switch (this.mappedFields[key].formatter) {
      case ResponsiveSearchFiltersTrackerFormatter.multiValue:
        return value.reduce((acc: any, singleValue: any) => {
          acc[`${fieldName}[${singleValue}]`] = singleValue;
          return acc;
        }, {} as any);
      case ResponsiveSearchFiltersTrackerFormatter.booleanToString:
        return { [fieldName]: value ? 'true' : 'false' };
      default:
        return { [fieldName]: value };
    }
  }
}
